// src/pages/builder/HomeDemoPanel.js
//
// Layout Builder ▸ 🏠 Home page demo.
//
// 1. WHAT THE HOME PAGE PLAYS
//    Pick the sample topics (by slug) and publish them. "Publish" gathers each
//    topic's live content — topicLibrary (title, picture, video, song,
//    sing-along, lyrics), its game from quizzes/{slug} (or the static quiz
//    library), and its published sing-along timings from lyricSync — and
//    writes ONE public document, publicDemo/home. Public visitors aren't
//    signed in and can't read those collections, so the copy is what lets the
//    home page play real content without opening anything else up.
//    Re-publish after changing a topic's media or game.
//    Reads topicLibrary / quizzes / lyricSync; writes only publicDemo/home.
//
// 2. WHO TRIED IT
//    The tracker the trackDemo Cloud Function keeps: unique visitors, what
//    they clicked, which games they played and how often, day by day.
//    Reads demoStats and demoVisitors; writes nothing.

import React, { useCallback, useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../../firebase/firebaseConfig";
import { getLibraryTopic } from "../../services/topicLibraryService";
import { getLyricSync, isSyncLive } from "../../services/lyricSyncService";
import { getQuizBySlug } from "../../data/quiz-library";
import { DEMO_FALLBACK_TOPICS } from "../../containers/preview/demoContent";
import FunPopup from "../../components/common/FunPopup";

const SUBJECT_OPTIONS = [
  ["science", "Science"],
  ["socialstudies", "Social Studies"],
  ["math", "Math"],
];
const MAX_TOPICS = 9;
const BLANK_ROW = { slug: "", subject: "science", grade: "", standardNumber: "", description: "" };

const DEFAULT_ROWS = DEMO_FALLBACK_TOPICS.map((t) => ({
  slug: t.slug,
  subject: t.subject,
  grade: t.grade,
  standardNumber: t.standardNumber,
  description: t.description,
}));

// Firestore Timestamps etc. → plain JSON, so the public document holds data only
const plain = (v) => {
  try {
    return JSON.parse(
      JSON.stringify(v, (k, val) => {
        if (val && typeof val === "object" && typeof val.toDate === "function") return undefined;
        if (["updatedAt", "createdAt", "publishedAt", "updatedBy"].includes(k)) return undefined;
        return val;
      })
    );
  } catch {
    return null;
  }
};

const readQuiz = async (ids) => {
  for (const id of ids.filter(Boolean)) {
    try {
      const snap = await getDoc(doc(db, "quizzes", id));
      if (snap.exists() && (snap.data().questions || []).length) return { id, ...snap.data(), from: "quizzes" };
    } catch {
      /* try the next id */
    }
  }
  return null;
};

/** One row → the topic object the home page plays, plus what was found. */
const buildTopic = async (row) => {
  const slug = String(row.slug || "").trim();
  const lib = await getLibraryTopic(slug);
  const builtIn = DEMO_FALLBACK_TOPICS.find((t) => t.slug === slug) || null;
  if (!lib && !builtIn) return { error: "not in the Topic library" };

  const base = lib || builtIn;
  const quiz = (await readQuiz([slug, lib?.id, lib?.slug, builtIn?.quizSlug])) || null;
  const stat = !quiz ? getQuizBySlug(slug) || (builtIn?.quizSlug ? getQuizBySlug(builtIn.quizSlug) : null) : null;
  const q = quiz || stat;

  const musicUrl = base.musicUrl || base.music || "";
  let sync = null;
  try {
    const s = await getLyricSync(lib?.id || slug);
    if (s && isSyncLive(s, musicUrl)) sync = plain(s);
  } catch {
    sync = null;
  }

  const topic = {
    slug,
    title: base.title || slug,
    subject: row.subject || base.subject || "science",
    grade: String(row.grade || "").trim(),
    standardNumber: String(row.standardNumber || "").trim(),
    description: String(row.description || "").trim() || base.description || "",
    imageUrl: lib?.imageUrl || "",
    videoUrl: String(base.videoUrl || base.video || "").trim(),
    musicUrl: String(musicUrl).trim(),
    singAlongUrl: String(lib?.singAlongUrl || "").trim(),
    lyrics: Array.isArray(base.lyrics) ? base.lyrics.map((l) => String(l ?? "")) : [],
    sync,
    gameType: q?.gameType || lib?.gameType || "multipleChoice",
    bonusRoundType: q?.bonusRoundType || lib?.bonusRoundType || builtIn?.bonusRoundType || "diamondTap",
    placeholderImage: q?.placeholderImage || "",
    questions: plain(q?.questions || []) || [],
    quizSlug: "",
  };

  return {
    topic,
    found: {
      library: Boolean(lib),
      video: Boolean(topic.videoUrl),
      music: Boolean(topic.musicUrl),
      singalong: Boolean(topic.singAlongUrl),
      sync: Boolean(sync),
      questions: topic.questions.length,
      quizFrom: quiz ? "quizzes" : stat ? "static library" : "",
    },
  };
};

/* ------------------------------------------------------------------ */
/* Tracker                                                             */
/* ------------------------------------------------------------------ */

const dayKey = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const lastDays = (n) =>
  Array.from({ length: n }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - i);
    return dayKey(d);
  });

const TILES = [
  ["visitors", "👥 Visitors", "Different browsers that reached the demo"],
  ["view", "👀 Saw the demo", "Page loads that scrolled to the demo"],
  ["game", "🎮 Games started", ""],
  ["game_finish", "🏁 Games finished", ""],
  ["video", "🎬 Videos opened", ""],
  ["music", "🎵 Songs opened", ""],
  ["singalong", "🎤 Sing-alongs opened", ""],
  ["avatar", "🎨 Avatar maker", ""],
  ["classmates", "👩‍🏫 Meet the class", ""],
  ["leaderboard", "🏆 Leaderboard", ""],
  ["controls", "🎛️ Educator controls", ""],
  ["signup", "✨ Free-trial clicks", ""],
];

const n = (v) => Number(v) || 0;

const Tracker = () => {
  const [state, setState] = useState({ loading: true });
  const [range, setRange] = useState(14);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const totalsSnap = await getDoc(doc(db, "demoStats", "totals"));
      const keys = lastDays(range);
      const daySnaps = await Promise.all(keys.map((k) => getDoc(doc(db, "demoStats", `day-${k}`))));
      const days = keys.map((k, i) => ({ day: k, ...(daySnaps[i].exists() ? daySnaps[i].data() : {}) }));
      let top = [];
      try {
        const vs = await getDocs(query(collection(db, "demoVisitors"), orderBy("totalPlays", "desc"), limit(10)));
        top = vs.docs.map((d) => ({ id: d.id, ...d.data() })).filter((v) => n(v.totalPlays) > 0);
      } catch {
        top = [];
      }
      setState({ loading: false, totals: totalsSnap.exists() ? totalsSnap.data() : null, days, top });
    } catch (e) {
      setState({ loading: false, error: e?.code === "permission-denied" ? "The demo tracker rules aren't deployed yet (firestore.rules)." : e?.message || "Couldn't load." });
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.loading && !state.totals && !state.error) return <p className="lb-hint">Loading the tracker…</p>;
  if (state.error) return <p className="hdm-err">{state.error}</p>;

  const t = state.totals || {};
  const ev = t.events || {};
  const topics = Object.entries(t.topics || {}).sort((a, b) => n(b[1].game) + n(b[1].video) + n(b[1].music) - (n(a[1].game) + n(a[1].video) + n(a[1].music)));
  const days = state.days || [];
  const sumDays = (key) => days.reduce((s, d) => s + (key === "visitors" ? n(d.visitors) : n(d.events?.[key])), 0);
  const peak = Math.max(1, ...days.map((d) => n(d.visitors)));

  return (
    <div className="hdm-tracker">
      <div className="hdm-row-head">
        <h3>Who tried it</h3>
        <select className="hdm-input hdm-select" value={range} onChange={(e) => setRange(Number(e.target.value))} aria-label="Days shown">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={load} disabled={state.loading}>
          {state.loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {!state.totals ? (
        <p className="lb-hint">Nobody has used the demo yet (or the trackDemo function isn't deployed). Visits from localhost aren't counted.</p>
      ) : (
        <>
          <div className="hdm-tiles">
            {TILES.map(([key, label, hint]) => (
              <div key={key} className="hdm-tile" title={hint || undefined}>
                <span>{label}</span>
                <b>{key === "visitors" ? n(t.visitors) : n(ev[key])}</b>
                <small>
                  {sumDays(key)} in the last {range} days
                </small>
              </div>
            ))}
          </div>

          <h4>By topic (all time)</h4>
          <div className="hdm-scroll">
            <table className="hdm-table">
              <thead>
                <tr>
                  <th>Topic</th>
                  <th title="opened / actually played">🎬 Video</th>
                  <th title="opened / actually played">🎵 Song</th>
                  <th title="opened / actually played">🎤 Sing-along</th>
                  <th>🎮 Started</th>
                  <th>🏁 Finished</th>
                  <th>⭐ Avg score</th>
                </tr>
              </thead>
              <tbody>
                {topics.length === 0 && (
                  <tr>
                    <td colSpan={7}>No topic clicks yet.</td>
                  </tr>
                )}
                {topics.map(([slug, r]) => (
                  <tr key={slug}>
                    <td>
                      <b>{r.title || slug}</b>
                      <small> {slug}</small>
                    </td>
                    <td>
                      {n(r.video)} / {n(r.video_play)}
                    </td>
                    <td>
                      {n(r.music)} / {n(r.music_play)}
                    </td>
                    <td>
                      {n(r.singalong)} / {n(r.singalong_play)}
                    </td>
                    <td>{n(r.game)}</td>
                    <td>{n(r.game_finish)}</td>
                    <td>{n(r.game_finish) ? Math.round(n(r.scoreSum) / n(r.game_finish)) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="lb-hint">“opened / played”: clicks on the button, then how many actually pressed play.</p>

          <h4>Day by day</h4>
          <div className="hdm-scroll">
            <table className="hdm-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>👥 Visitors</th>
                  <th>👀 Saw it</th>
                  <th>🎮 Games</th>
                  <th>🏁 Finished</th>
                  <th>🎬🎵🎤 Media</th>
                  <th>✨ Trial clicks</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const e = d.events || {};
                  return (
                    <tr key={d.day}>
                      <td>{d.day}</td>
                      <td>
                        <span className="hdm-bar" title={`${n(d.visitors)} visitors on ${d.day}`}>
                          <i style={{ width: `${(n(d.visitors) / peak) * 100}%` }} />
                        </span>
                        {n(d.visitors)}
                      </td>
                      <td>{n(e.view)}</td>
                      <td>{n(e.game)}</td>
                      <td>{n(e.game_finish)}</td>
                      <td>{n(e.video) + n(e.music) + n(e.singalong)}</td>
                      <td>{n(e.signup)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h4>Most games played by one visitor</h4>
          {state.top.length === 0 ? (
            <p className="lb-hint">No finished games yet.</p>
          ) : (
            <div className="hdm-scroll">
              <table className="hdm-table">
                <thead>
                  <tr>
                    <th>Visitor</th>
                    <th>Games finished</th>
                    <th>Played most</th>
                    <th>Days visited</th>
                    <th>First / last visit</th>
                  </tr>
                </thead>
                <tbody>
                  {state.top.map((v) => {
                    const fav = Object.entries(v.plays || {}).sort((a, b) => n(b[1]) - n(a[1]))[0];
                    const favTitle = fav ? t.topics?.[fav[0]]?.title || fav[0] : "—";
                    return (
                      <tr key={v.id}>
                        <td>
                          <code>{v.id.slice(-6)}</code>
                        </td>
                        <td>{n(v.totalPlays)}</td>
                        <td>{fav ? `${favTitle} ×${n(fav[1])}` : "—"}</td>
                        <td>{n(v.daysVisited) || 1}</td>
                        <td>
                          {v.firstDay || "?"} → {v.lastDay || "?"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="lb-hint">A visitor is one browser (a random id saved in it) — no names, emails or IP addresses are kept.</p>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */

const HomeDemoPanel = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [published, setPublished] = useState(null); // { count, at }
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState([]);
  const [pop, setPop] = useState(null);

  useEffect(() => {
    if (!open || rows) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "publicDemo", "home"));
        if (snap.exists() && (snap.data().topics || []).length) {
          const d = snap.data();
          setRows(d.topics.map((t) => ({ slug: t.slug, subject: t.subject, grade: t.grade || "", standardNumber: t.standardNumber || "", description: t.rowDescription || "" })));
          setPublished({ count: d.topics.length, at: d.updatedAt?.toDate?.() || null });
          return;
        }
      } catch {
        /* rules not deployed yet */
      }
      setRows(DEFAULT_ROWS);
      setPublished(null);
    })();
  }, [open, rows]);

  const setRow = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) =>
    setRows((r) => {
      const j = i + d;
      if (j < 0 || j >= r.length) return r;
      const next = [...r];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const publish = async () => {
    const list = (rows || []).filter((r) => String(r.slug || "").trim());
    if (!list.length) {
      setPop({ tone: "bad", title: "No topics", text: "Add at least one topic slug first." });
      return;
    }
    setBusy(true);
    setReport([]);
    try {
      const results = [];
      for (const row of list) {
        // eslint-disable-next-line no-await-in-loop
        const r = await buildTopic(row);
        results.push({ row, ...r });
        setReport([...results]);
      }
      const ok = results.filter((r) => r.topic);
      if (!ok.length) throw new Error("None of these slugs were found.");

      const topics = ok.map((r) => ({ ...r.topic, rowDescription: String(r.row.description || "").trim() }));
      const size = JSON.stringify(topics).length;
      if (size > 900000) throw new Error(`That's too much to publish at once (${Math.round(size / 1024)} KB). Use fewer topics.`);

      await setDoc(doc(db, "publicDemo", "home"), {
        topics,
        updatedAt: serverTimestamp(),
        updatedBy: getAuth().currentUser?.email || "",
      });
      setPublished({ count: topics.length, at: new Date() });
      const missing = results.length - ok.length;
      setPop({
        tone: "ok",
        icon: "🏠",
        title: "Published to the home page!",
        text: `${topics.length} topic${topics.length === 1 ? "" : "s"} are live in the demo.${missing ? ` ${missing} slug${missing === 1 ? " was" : "s were"} skipped — see the list.` : ""}`,
      });
    } catch (e) {
      setPop({
        tone: "bad",
        title: "Couldn't publish",
        text: e?.code === "permission-denied" ? "The publicDemo rule isn't deployed yet (firestore.rules), or this account isn't an admin." : e?.message || "Please try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    setBusy(true);
    try {
      await deleteDoc(doc(db, "publicDemo", "home"));
      setPublished(null);
      setPop({ tone: "ok", title: "Back to the built-in samples", text: "The home page now uses the six built-in sample topics." });
    } catch (e) {
      setPop({ tone: "bad", title: "Couldn't remove it", text: e?.message || "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="lb-card hdm-panel">
      <div className="lb-card-head">
        <span className="lb-step hdm-step">🏠</span>
        <div>
          <h2>Home page demo</h2>
          <p className="lb-hint">
            The free “try it” class on the public home page: which topics it plays, and who tried what. Publishing writes only publicDemo/home; the tracker only reads.
          </p>
        </div>
        <div className="hdm-head-actions">
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Open"}
          </button>
        </div>
      </div>

      {open && (
        <>
          <Tracker />

          <div className="hdm-topics">
            <div className="hdm-row-head">
              <h3>What the home page plays</h3>
              <span className="hdm-status">
                {published ? `Live: ${published.count} published topic${published.count === 1 ? "" : "s"}${published.at ? ` · ${published.at.toLocaleString()}` : ""}` : "Live: the built-in sample topics (nothing published yet)"}
              </span>
            </div>
            <p className="lb-hint">
              Use Topic library slugs. Grade and standard are what the card shows. Leave the description blank to use the library's. Media, sing-along timings and the game are copied in when
              you publish — publish again after changing any of them.
            </p>

            {!rows ? (
              <p className="lb-hint">Loading…</p>
            ) : (
              <div className="hdm-rows">
                {rows.map((r, i) => {
                  const rep = report.find((x) => x.row === r);
                  return (
                    <div key={i} className="hdm-row">
                      <span className="hdm-num">{i + 1}</span>
                      <input className="hdm-input" value={r.slug} onChange={(e) => setRow(i, { slug: e.target.value.trim() })} placeholder="slug, e.g. landforms" aria-label="Topic slug" />
                      <select className="hdm-input hdm-select" value={r.subject} onChange={(e) => setRow(i, { subject: e.target.value })} aria-label="Subject">
                        {SUBJECT_OPTIONS.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                      <input className="hdm-input hdm-short" value={r.grade} onChange={(e) => setRow(i, { grade: e.target.value })} placeholder="3rd" aria-label="Grade" />
                      <input className="hdm-input hdm-short" value={r.standardNumber} onChange={(e) => setRow(i, { standardNumber: e.target.value })} placeholder="3.OA.7" aria-label="Standard" />
                      <input className="hdm-input hdm-wide" value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} placeholder="Card description (blank = library's)" aria-label="Description" />
                      <span className="hdm-row-btns">
                        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">
                          ↑
                        </button>
                        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down">
                          ↓
                        </button>
                        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label="Remove">
                          ✕
                        </button>
                      </span>
                      {rep && (
                        <span className={`hdm-found ${rep.error ? "is-bad" : ""}`}>
                          {rep.error ? (
                            `✗ ${rep.error}`
                          ) : (
                            <>
                              <b>{rep.topic.title}</b> · {rep.found.video ? "✓" : "✗"} video · {rep.found.music ? "✓" : "✗"} song{rep.found.sync ? " (timed lyrics)" : ""} ·{" "}
                              {rep.found.singalong ? "✓" : "—"} sing-along · {rep.found.questions ? `✓ game, ${rep.found.questions} questions (${rep.found.quizFrom})` : "✗ no game"}
                              {!rep.found.library && " · not in the library, built-in media used"}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="hdm-actions">
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setRows((r) => [...(r || []), { ...BLANK_ROW }])} disabled={!rows || rows.length >= MAX_TOPICS}>
                + Add topic
              </button>
              <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={() => setRows(DEFAULT_ROWS)} disabled={busy}>
                Reset to the six samples
              </button>
              <span className="hdm-spacer" />
              {published && (
                <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" onClick={unpublish} disabled={busy}>
                  Use built-in samples
                </button>
              )}
              <button type="button" className="lb-btn" onClick={publish} disabled={busy || !rows}>
                {busy ? "Publishing…" : "🏠 Publish to home page"}
              </button>
            </div>
          </div>
        </>
      )}

      <FunPopup pop={pop} onClose={() => setPop(null)} />

      <style>{`
        .lb-page .hdm-panel .hdm-step { background: var(--lb-violet, #7c3aed); color: #fff; }
        .lb-page .hdm-panel .hdm-head-actions { margin-left: auto; }
        .lb-page .hdm-panel h3 { margin: 0; font-size: 16px; color: var(--lb-ink); }
        .lb-page .hdm-panel h4 { margin: 18px 0 8px; font-size: 14px; color: var(--lb-ink); }
        .lb-page .hdm-panel .hdm-row-head { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 6px 0 10px; }
        .lb-page .hdm-panel .hdm-status { font-size: 13px; font-weight: 700; color: var(--lb-ink-soft); }
        .lb-page .hdm-panel .hdm-tracker, .lb-page .hdm-panel .hdm-topics { border: 1px solid var(--lb-line); border-radius: 14px; padding: 14px 15px; background: var(--lb-tint); margin-top: 12px; }
        .lb-page .hdm-panel .hdm-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
        .lb-page .hdm-panel .hdm-tile { display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border-radius: 12px; background: var(--lb-surface, #fff); border: 1px solid var(--lb-line); }
        .lb-page .hdm-panel .hdm-tile span { font-size: 12.5px; font-weight: 700; color: var(--lb-ink-soft); }
        .lb-page .hdm-panel .hdm-tile b { font-size: 24px; color: var(--lb-ink); font-variant-numeric: tabular-nums; }
        .lb-page .hdm-panel .hdm-tile small { font-size: 11.5px; color: var(--lb-ink-soft); }
        .lb-page .hdm-panel .hdm-scroll { overflow-x: auto; }
        .lb-page .hdm-panel .hdm-table { width: 100%; border-collapse: collapse; background: var(--lb-surface, #fff); border-radius: 12px; overflow: hidden; font-size: 13px; }
        .lb-page .hdm-panel .hdm-table th, .lb-page .hdm-panel .hdm-table td { padding: 7px 10px; border-bottom: 1px solid var(--lb-line); text-align: left; white-space: nowrap; color: var(--lb-ink); font-variant-numeric: tabular-nums; }
        .lb-page .hdm-panel .hdm-table th { font-size: 12px; color: var(--lb-ink-soft); background: var(--lb-tint); }
        .lb-page .hdm-panel .hdm-table td small { color: var(--lb-ink-soft); }
        .lb-page .hdm-panel .hdm-bar { display: inline-block; width: 90px; height: 8px; margin-right: 8px; border-radius: 4px; background: var(--lb-line); vertical-align: middle; overflow: hidden; }
        .lb-page .hdm-panel .hdm-bar i { display: block; height: 100%; border-radius: 4px; background: var(--lb-violet, #7c3aed); }
        .lb-page .hdm-panel .hdm-rows { display: grid; gap: 8px; }
        .lb-page .hdm-panel .hdm-row { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 8px; border-radius: 12px; background: var(--lb-surface, #fff); border: 1px solid var(--lb-line); }
        .lb-page .hdm-panel .hdm-num { width: 22px; text-align: center; font-weight: 800; color: var(--lb-ink-soft); }
        .lb-page .hdm-panel .hdm-input { padding: 6px 9px; border: 1px solid var(--lb-line); border-radius: 9px; font: inherit; font-size: 13px; min-width: 0; width: 150px; background: #fff; color: var(--lb-ink); }
        .lb-page .hdm-panel .hdm-select { width: auto; }
        .lb-page .hdm-panel .hdm-short { width: 80px; }
        .lb-page .hdm-panel .hdm-wide { flex: 1; min-width: 200px; }
        .lb-page .hdm-panel .hdm-row-btns { display: inline-flex; gap: 4px; }
        .lb-page .hdm-panel .hdm-found { flex-basis: 100%; padding-left: 28px; font-size: 12.5px; color: #15803d; }
        .lb-page .hdm-panel .hdm-found.is-bad { color: var(--lb-red, #dc2626); }
        .lb-page .hdm-panel .hdm-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
        .lb-page .hdm-panel .hdm-spacer { flex: 1; }
        .lb-page .hdm-panel .hdm-err { color: var(--lb-red, #dc2626); font-weight: 700; }
      `}</style>
    </section>
  );
};

export default HomeDemoPanel;
